/**
 * Pure HTML rendering helpers for KB list view.
 * Extracted from kb_list.ts to keep that file under the 300-line limit.
 */

import { esc, relTime } from '../../shared/rendering.js'
import { action } from '../components/action-delegation.js'
import { kbTypeColor } from './kb_types.js'
import type { KBEntry } from '../../types/api.js'

export function btnStyle(active: boolean): string {
  const base =
    'font-family:var(--mono);font-size:10px;padding:3px 10px;border-radius:4px;border:none;cursor:pointer;'
  return `${base}background:${active ? 'var(--s4)' : 'transparent'};color:${active ? 'var(--t1)' : 'var(--t3)'}`
}

export function renderKBRows(
  pageItems: readonly KBEntry[],
  filtered: readonly KBEntry[],
  tagFilter: ReadonlySet<string>,
  readCounts: Record<string, number> = {},
): string {
  if (filtered.length === 0) {
    return `<div style="text-align:center;padding:48px 20px;color:var(--t4)"><div style="font-family:var(--mono);font-size:12px;margin-bottom:12px">No entries match the active filters.</div><button style="font-family:var(--mono);font-size:11px;padding:5px 16px;border-radius:6px;border:1px solid var(--bd);cursor:pointer;background:var(--s2);color:var(--t2)" ${action('clearKbFilters')}>Clear filters</button></div>`
  }
  return pageItems
    .map((e) => {
      const c = kbTypeColor(e.type)
      const tags = (e.tags ?? [])
        .map((t) => {
          const tagActive = tagFilter.has(t)
          return `<button aria-pressed="${tagActive}" aria-label="Filter by tag ${esc(t)}" style="font-family:var(--mono);font-size:9px;padding:1px 5px;border-radius:3px;border:none;cursor:pointer;background:${tagActive ? 'var(--cyn-d)' : 'var(--s3)'};color:${tagActive ? 'var(--cyn)' : 'var(--t3)'}" ${action('toggleKbTag', t)}>${esc(t)}</button>`
        })
        .join('')
      const body = e.body || ''
      const preview = body.slice(0, 90).replace(/\n/g, ' ')
      const reads = readCounts[e.id]
      const readsBadge =
        reads !== undefined
          ? `<span style="font-family:var(--mono);font-size:9px;padding:1px 6px;border-radius:99px;background:var(--grn-d);color:var(--grn)" title="${reads} read${reads !== 1 ? 's' : ''}">${reads} reads</span>`
          : ''
      return `<div class="kb-row" aria-label="${esc(e.title)}" style="padding:10px 14px;background:var(--s1);border:1px solid var(--bd);border-left:3px solid var(--${c});border-radius:8px;margin-bottom:4px;cursor:pointer;transition:background .12s" tabindex="0" role="link" ${action('navigate', `/kb/${e.id}`)}><div style="display:flex;align-items:center;gap:8px;margin-bottom:4px"><span style="font-family:var(--mono);font-size:9px;padding:1px 5px;border-radius:3px;background:var(--${c}-d);color:var(--${c})">${esc(e.type)}</span><span style="font-family:var(--mono);font-size:10px;color:var(--t4)">${esc(e.id)}</span><span style="font-size:13px;font-weight:500;flex:1">${esc(e.title)}</span><span style="font-family:var(--mono);font-size:10px;color:var(--t4)">${esc(relTime(e.updated_at))}</span>${readsBadge}<span style="font-family:var(--mono);font-size:9px;padding:1px 6px;border-radius:99px;background:var(--s3);color:var(--t4)">${esc(e.status)}</span></div><div style="font-size:11px;color:var(--t3);line-height:1.5;margin-bottom:5px">${esc(preview)}${body.length > 90 ? '\u2026' : ''}</div><div style="display:flex;gap:3px;flex-wrap:wrap">${tags}</div></div>`
    })
    .join('')
}

export function renderPaginationBar(page: number, totalPages: number): string {
  if (totalPages <= 1) {
    return ''
  }
  const st = (disabled: boolean) =>
    `font-family:var(--mono);font-size:11px;padding:4px 12px;border-radius:5px;border:1px solid var(--bd);cursor:${disabled ? 'default' : 'pointer'};background:var(--s2);color:${disabled ? 'var(--t4)' : 'var(--t2)'};opacity:${disabled ? '.4' : '1'}`
  return `<div style="display:flex;align-items:center;justify-content:center;gap:12px;margin-top:14px;padding:10px 0"><button aria-label="Previous page" ${page === 0 ? 'disabled aria-disabled="true"' : ''} style="${st(page === 0)}" ${action('setKbPage', String(page - 1))}><span aria-hidden="true">\u2190</span> Prev</button><span style="font-family:var(--mono);font-size:11px;color:var(--t3)" aria-live="polite" aria-atomic="true">Page ${page + 1} of ${totalPages}</span><button aria-label="Next page" ${page >= totalPages - 1 ? 'disabled aria-disabled="true"' : ''} style="${st(page >= totalPages - 1)}" ${action('setKbPage', String(page + 1))}>Next <span aria-hidden="true">\u2192</span></button></div>`
}

export function renderTagCloud(
  entries: readonly KBEntry[],
  tagFilter: ReadonlySet<string>,
): string {
  const tagCounts: Record<string, number> = {}
  entries.forEach((e) => {
    ;(e.tags ?? []).forEach((t) => {
      tagCounts[t] = (tagCounts[t] || 0) + 1
    })
  })
  const allTags = Object.entries(tagCounts).sort((a, b) => b[1] - a[1])
  if (allTags.length === 0) return ''
  const tagBtns = allTags
    .map(([tag, count]) => {
      const active = tagFilter.has(tag)
      return `<button aria-pressed="${active}" style="font-family:var(--mono);font-size:9px;padding:2px 9px;border-radius:99px;border:1px solid ${active ? 'var(--cyn)' : 'var(--bd)'};cursor:pointer;background:${active ? 'var(--cyn-d)' : 'transparent'};color:${active ? 'var(--cyn)' : 'var(--t4)'};transition:all .12s" ${action('toggleKbTag', tag)} aria-label="Filter by tag ${esc(tag)}, ${String(count)} entr${esc(count !== 1 ? 'ies' : 'y')}">${esc(tag)} <span style="opacity:.5;font-size:8px">${String(count)}</span></button>`
    })
    .join('')
  return `<div role="group" aria-label="Filter by tag" style="display:flex;gap:4px;flex-wrap:wrap;margin-bottom:12px">${tagBtns}</div>`
}

/** KB view tab bar. `view` is the active tab key ('list' | 'usage'). */
export function renderTabBar(view: string): string {
  const listActive = view === 'list'
  const usageActive = view === 'usage'
  return `<div role="tablist" aria-label="KB views" style="display:flex;gap:0;margin-bottom:0;border-bottom:1px solid var(--bd)">
    <button role="tab" aria-selected="${listActive}" style="padding:6px 14px;border:none;background:${listActive ? 'var(--s3)' : 'transparent'};color:var(--${listActive ? 'cyn' : 't3'});cursor:pointer;font-size:12px;border-radius:6px 6px 0 0" ${action('setKbView', 'list')}>Entries</button>
    <button role="tab" aria-selected="${usageActive}" style="padding:6px 14px;border:none;background:${usageActive ? 'var(--s3)' : 'transparent'};color:var(--${usageActive ? 'cyn' : 't3'});cursor:pointer;font-size:12px;border-radius:6px 6px 0 0" ${action('setKbView', 'usage')}>Usage</button>
  </div>`
}
